import {
  Box,
  Button,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Drawer } from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import WebFormBuilder from './WebFormBuilder';
import WebFormPreview from './WebFormPreview';
import WebFormJson from './WebFormJson';
import { RootState } from '../../../utils/redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createWebFormApi,
  submitWebFormApi,
  updateWebFormApi,
} from '../../../utils/apis';
import { resetWebForm } from '../../../utils/redux/slices/webformSlice';
import { useDashboard } from '../../../hooks/useDashboard';
import { ICreateWebForm, IWebFormField } from '../../../schemas/apis/interface';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';

interface WebFormFunctionDrawerProps {
  open: boolean;
  onClose: () => void;
  webformData: any;
  isEditing: boolean;
}

const WebFormFunctionDrawer = ({
  open,
  onClose,
  webformData,
  isEditing,
}: WebFormFunctionDrawerProps) => {
  const [activeTab, setActiveTab] = useState('form-builder');
  const [apiResponse, setApiResponse] = useState<any>(null);
  const dispatch = useDispatch();
  const { selectedAssistant } = useDashboard();
  const webformState = useSelector((state: RootState) => state.webform);
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  const { mutate: createWebForm, isPending: isCreatingWebFormPending } =
    useMutation({
      mutationFn: createWebFormApi,
      onSuccess: () => {
        showAlert('Webform function created successfully', 'success');
        onClose();
        dispatch(resetWebForm());
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        if (selectedAssistant?._id) {
          queryClient.refetchQueries({
            queryKey: ['agent', selectedAssistant._id],
          });
        }
      },
      onError: (error: any) => {
        showAlert('Error creating webform', 'error');
        console.error('Error creating webform:', error);
      },
    });

  const { mutate: updateWebForm, isPending: isUpdatingWebFormPending } =
    useMutation({
      mutationFn: (data: {
        agentId: string;
        functionName: string;
        updateData: Partial<ICreateWebForm>;
      }) => updateWebFormApi(data.agentId, data.functionName, data.updateData),
      onSuccess: () => {
        showAlert('Webform function updated successfully', 'success');
        onClose();
        dispatch(resetWebForm());
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        if (selectedAssistant?._id) {
          queryClient.refetchQueries({
            queryKey: ['agent', selectedAssistant._id],
          });
        }
      },
      onError: (error: any) => {
        showAlert('Error updating webform', 'error');
        console.error('Error updating webform:', error);
      },
    });

  // Submit web form mutation
  const { mutateAsync: submitWebForm, isPending: isSubmitPending } =
    useMutation({
      mutationFn: ({
        agentId,
        functionName,
        formData,
      }: {
        agentId: string;
        functionName: string;
        formData: Record<string, any>;
      }) => {
        return submitWebFormApi(agentId, functionName, formData);
      },
      onSuccess: response => {
        showAlert('Form submitted successfully', 'success');
        setApiResponse(response.data);
        setActiveTab('json');
      },
      onError: error => {
        console.error('Error submitting web form:', error);
        showAlert('Error submitting web form', 'error');
      },
    });

  const handleSaveFunction = () => {
    // Transform FormField to IWebFormField
    const fields: IWebFormField[] = webformState.formFields.map(field => {
      const webFormField: IWebFormField = {
        name: field.name,
        label: field.label,
        type: field.type as any,
        required: field.required,
        placeholder: field.placeholder,
      };

      // Add options for select fields
      if (
        (field.type === 'select' ||
          field.type === 'checkbox' ||
          field.type === 'radio') &&
        field.options &&
        field.options.length > 0
      ) {
        webFormField.options = field.options.map(option => ({
          value: option,
          label: option,
        }));
      }

      return webFormField;
    });

    // Create the web form payload
    const webFormData: ICreateWebForm = {
      agentId: selectedAssistant?._id || '',
      name: webformState.functionName,
      description: webformState.functionDescription,
      function_type: 'webform',
      parameters: {
        type: 'object',
        properties: Object.fromEntries(
          fields.map(field => [
            field.name,
            {
              type:
                field.type === 'select' || field.type === 'checkbox'
                  ? 'array'
                  : 'string',
              description: field.label,
            },
          ])
        ),
        required: fields
          .filter(field => field.required)
          .map(field => field.name),
      },
      webform_config: {
        title: webformState.formConfig.formTitle,
        description: webformState.formConfig.formDescription || '',
        submit_button_text:
          webformState.formConfig.submitButtonText || 'Submit',
        success_message:
          webformState.formConfig.successMessage ||
          'Form submitted successfully!',
        fields: fields,
      },
    };

    if (isEditing && webformData) {
      // For update, compare with original data and only send changed fields
      const updatedFields: Partial<ICreateWebForm> = {};

      // Add basic fields that might have changed
      if (webformState.functionName !== webformData.function_name) {
        updatedFields.name = webformState.functionName;
      }

      if (
        webformState.functionDescription !== webformData.function_description
      ) {
        updatedFields.description = webformState.functionDescription;
      }

      if (webformState.formConfig.formTitle !== webformData.display_name) {
        updatedFields.webform_config = {
          ...(updatedFields.webform_config || {}),
          title: webformState.formConfig.formTitle,
          description: webformState.formConfig.formDescription || '',
          success_message:
            webformState.formConfig.successMessage ||
            'Form submitted successfully!',
          submit_button_text:
            webformState.formConfig.submitButtonText || 'Submit',
          fields: fields,
        };
      }

      // Compare form fields to see if they've changed
      const originalFields = webformData.fields || [];
      const fieldsChanged =
        JSON.stringify(
          fields.map(f => ({ ...f, options: f.options || [] }))
        ) !==
        JSON.stringify(
          originalFields.map((f: any) => ({ ...f, options: f.options || [] }))
        );
      if (fieldsChanged) {
        updatedFields.webform_config = {
          ...(updatedFields.webform_config || {}),
          title: webformState.formConfig.formTitle || '',
          description: webformState.formConfig.formDescription || '',
          submit_button_text:
            webformState.formConfig.submitButtonText || 'Submit',
          success_message:
            webformState.formConfig.successMessage ||
            'Form submitted successfully!',
          fields: fields,
        };

        // Update parameters when fields change
        updatedFields.parameters = webFormData.parameters;
      }

      // Compare webform_config
      const originalConfig = webformData.webform_config || {};
      const newConfig = webFormData.webform_config;

      const configChanged =
        originalConfig.title !== newConfig?.title ||
        originalConfig.description !== newConfig?.description ||
        originalConfig.success_message !== newConfig?.success_message ||
        originalConfig.submit_button_text !==
          webformState.formConfig.submitButtonText;

      if (configChanged) {
        updatedFields.webform_config = {
          ...(updatedFields.webform_config || {}),
          title: webformState.formConfig.formTitle,
          description: webformState.formConfig.formDescription || '',
          success_message:
            webformState.formConfig.successMessage ||
            'Form submitted successfully!',
          submit_button_text:
            webformState.formConfig.submitButtonText || 'Submit',
          fields: updatedFields.webform_config?.fields || fields,
        };
      }

      // Always include agentId and function_type
      updatedFields.agentId = selectedAssistant?._id || '';
      updatedFields.function_type = 'webform';

      // Update with only changed fields
      updateWebForm({
        agentId: selectedAssistant?._id || '',
        functionName: webformData.name,
        updateData: updatedFields,
      });
    } else {
      // Create new webform
      createWebForm(webFormData);
    }
  };

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: 'form-builder' | 'preview' | 'json'
  ) => {
    setActiveTab(newValue);
  };

  const isPending = isCreatingWebFormPending || isUpdatingWebFormPending;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: '637px',
              p: 0,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Sticky Header Section */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#FFFFFF',
            px: 2,
            pt: 2,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            {/* Close (Cancel) Button and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                }}
              >
                <X size={18} />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: 'custom.textBlue',
                  letterSpacing: '-0.2px',
                }}
              >
                {isEditing ? 'Edit' : 'Create'} Webform Function
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Save Button */}
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSaveFunction}
                disabled={isPending || !webformState.functionName}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: '6px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {isPending
                  ? isEditing
                    ? 'Updating...'
                    : 'Saving...'
                  : isEditing
                    ? 'Update Function'
                    : 'Save Function'}
              </Button>
            </Box>
          </Box>

          <Divider
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.08)',
              mb: 1,
              mx: -2,
            }}
          />

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mx: -2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="webhook function tabs"
              sx={{
                minHeight: 'unset',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 'unset',
                  py: 1,
                  px: 2,
                  mx: 0.5,
                },
              }}
            >
              <Tab label="Form Builder" value="form-builder" />
              <Tab label="Preview" value="preview" />
              <Tab label="JSON" value="json" />
            </Tabs>
          </Box>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            px: 2,
            pb: 2,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {/* Function Builder */}
          {activeTab === 'form-builder' && <WebFormBuilder />}

          {/* Test Response */}
          {activeTab === 'preview' && (
            <WebFormPreview
              handleSubmitApi={data =>
                submitWebForm({
                  agentId: selectedAssistant?._id || '',
                  functionName: webformState.functionName,
                  formData: data,
                })
              }
              isPending={isSubmitPending}
              isEditing={isEditing}
            />
          )}
          {activeTab === 'json' && (
            <WebFormJson
              apiResponse={apiResponse}
              isPending={isSubmitPending}
            />
          )}
        </Box>
      </Drawer>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default WebFormFunctionDrawer;
